/* eslint-disable @typescript-eslint/no-explicit-any */
/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import { useCallback, useState } from 'react';
import {
	FileError,
	FileRejection,
	useDropzone,
} from 'react-dropzone';
import { Box, Image, rem, Text } from '@mantine/core';
import SingleFileUploadWithProgress from './SingleFileUploadWithProgress';
import ErrorSingleFileUploadWithProgress from './ErrorSingleFileUploadWithProgress';

import classes from './Dropzone.module.css';
import UploadIcon from '@assets/icons/upload.svg';
import clsx from 'clsx';
import { extractColumnNames } from '@utils/functions';
import { useTranslation } from 'react-i18next';

let currentId = 0;

export function getNewId() {
	return ++currentId;
}

export interface UploadableFile {
	id: number;
	file: File;
	errors: FileError[];
	url?: string;
}

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const Dropzone = (props: {
	handleUpload: (file: File) => void;
	handleReload: () => void;
}) => {
	const { t } = useTranslation();
	/* ******************************** States ******************************* */
	function onUpload(file: File, url: string) {
		props.handleUpload(file);
		setFiles((curr) =>
			curr.map((fw) => {
				if (fw.file === file) {
					return { ...fw, url };
				}
				return fw;
			})
		);
	}

	function onDelete(file: File) {
		setFiles((curr) =>
			curr.filter((fw) => fw.file !== file)
		);
	}

	/* ******************************** HOOKS ******************************* */
	const [files, setFiles] = useState<UploadableFile[]>(
		[]
	);

	const hasDuplicates = (array: string[]) => {
		return new Set(array).size !== array.length;
	};

	const onDrop = useCallback(
		(accFiles: File[], rejFiles: FileRejection[]) => {
			const processAccFile = (file: File) => {
				const fileData: any = {
					file,
					errors: [],
					id: getNewId(),
				};

				const reader = new FileReader();
				reader.onload = (e: any) => {
					const content = e.target.result;

					const columnsList = extractColumnNames(content);

					if (
						columnsList.some(
							(header: string) => header.trim() === ''
						)
					) {
						fileData.errors.push({
							code: 'csv-invalid-columns',
							message: t(
								'productions.notifications.emptyStringsColumns'
							),
						});
					}

					if (hasDuplicates(columnsList)) {
						fileData.errors.push({
							code: 'csv-invalid-columns',
							message: t(
								'productions.notifications.duplicateColumnsHeaders'
							),
						});
					}

					if (columnsList.length < 2) {
						fileData.errors.push({
							code: 'csv-invalid-columns',
							message: t(
								'productions.notifications.checkColumnsHeader'
							),
						});
					}

					setFiles((curr) => [...curr, fileData]);
				};
				reader.readAsText(file);
			};

			const mappedAcc = accFiles.map((file) => ({
				file,
				errors: [],
				id: getNewId(),
			}));
			const mappedRej = rejFiles.map((r) => ({
				...r,
				id: getNewId(),
			}));

			if (mappedRej.length) {
				setFiles((curr) => [
					...curr,
					...mappedAcc,
					...mappedRej,
				]);
			} else {
				if (accFiles.length) {
					processAccFile(accFiles[0]);
				}
			}
		},
		[]
	);

	const { getRootProps, getInputProps } = useDropzone({
		onDrop,
		accept: { 'text/csv': ['.csv'] },
		maxSize: 300 * 1024, // 300KB
	});
	/* ******************************** RENDERING ******************************* */
	return (
		<Box className={classes.container}>
			{files.length == 0 && (
				<Box
					data-testid="D2DtDXR7_Z5jdHi6B-dropzone"
					{...getRootProps({
						className: classes.dropzone,
					})}
					className={classes.dropzone}
				>
					<input {...getInputProps()} />
					<Image
						style={{
							width: rem(52),
							height: rem(52),
							color: 'var(--mantine-color-dimmed)',
						}}
						src={UploadIcon}
					/>
					<Text className={classes.text}>
						{t('form.dragAndDropCSV')}
						<Text
							span
							c="blue"
							inherit
							className={clsx(
								classes.text,
								classes.underline
							)}
						>
							{t('global.upload')}.
						</Text>
					</Text>

					<Text
						onClick={(e) => {
							e.stopPropagation();
						}}
						span
						inherit
						className={clsx(
							classes.disabledText,
							classes.underline
						)}
					>
						{t('form.downloadModal')}
					</Text>
				</Box>
			)}

			{files.map((fileWrapper) => (
				<Box key={fileWrapper.id}>
					{fileWrapper.errors.length ? (
						<ErrorSingleFileUploadWithProgress
							onDelete={onDelete}
							onUpload={onUpload}
							onReload={() => {
								setFiles([]);
								props.handleReload();
							}}
							file={fileWrapper.file}
							errors={fileWrapper.errors}
						/>
					) : (
						<SingleFileUploadWithProgress
							onDelete={onDelete}
							onUpload={onUpload}
							onReload={() => {
								setFiles([]);
								props.handleReload();
							}}
							file={fileWrapper.file}
						/>
					)}
				</Box>
			))}
		</Box>
	);
};
